import type { BlockEntity } from "../block-definitions.js";
import { manufacturing } from "./manufacturing.js";
import { banking } from "./banking.js";
import { confectionery } from "./confectionery.js";
import { airlines } from "./airlines.js";
import { media } from "./media.js";
import { healthcare } from "./healthcare.js";
import { pharmaceuticals } from "./pharmaceuticals.js";
import { retail } from "./retail.js";
import { industryInventory } from "./inventory.js";
import { commonAssumptions, type IndustryModel } from "./schema.js";

export const industryDefinitions: Record<string, BlockEntity[]> = {
  ...manufacturing, ...banking, ...confectionery, ...airlines,
  ...media, ...healthcare, ...pharmaceuticals, ...retail,
};
const sources = {
  isa: { title: "ISA-95 enterprise and manufacturing operations integration", url: "https://www.isa.org/standards-and-publications/isa-standards/isa-95-standard" },
  bian: { title: "BIAN Service Landscape", url: "https://bian.org/deliverables/service-landscape/" },
  gs1: { title: "GS1 traceability and EPCIS concepts", url: "https://www.gs1.org/standards/traceability" },
  iata: { title: "IATA airline retailing reference architecture", url: "https://www.iata.org/en/programs/airline-distribution/retailing/business-reference-architecture/" },
  cargo: { title: "IATA ONE Record cargo design concepts", url: "https://github.com/IATA-Cargo/ONE-Record" },
  iptc: { title: "IPTC NewsML-G2 content, packages and editorial metadata", url: "https://iptc.org/standards/newsml-g2/" },
  hl7: { title: "HL7 FHIR R5 resource boundaries", url: "https://hl7.org/fhir/R5/resourcelist.html" },
  ich: { title: "ICH Q10 Pharmaceutical Quality System, 4 June 2008", url: "https://database.ich.org/sites/default/files/Q10%20Guideline.pdf" },
};
const packs: Record<string, { industry: string; sources: (keyof typeof sources)[]; note: string }> = {
  MFG: { industry: "Manufacturing", sources: ["isa", "gs1"], note: "Production, quality, inventory and maintenance structures are examples informed by ISA concepts, not a complete ISA-95 schema." },
  BNK: { industry: "Banking", sources: ["bian"], note: "Structural banking examples only; no regulatory reporting, transaction authorization, payment network or accounting-policy implementation." },
  CNF: { industry: "Confectionery", sources: ["gs1", "isa"], note: "Traceability and recipe examples only; no food-safety thresholds, shelf-life claims or jurisdictional allergen requirements are supplied." },
  AIR: { industry: "Airlines", sources: ["iata", "cargo"], note: "Includes traditional reservations and tickets; these tables are not NDC, ONE Order or ONE Record interchange schemas, and do not perform operational safety checks." },
  MED: { industry: "Media", sources: ["iptc"], note: "Editorial works, versions, assets and rights are deliberately separate; this relational pack does not claim NewsML-G2 conformance." },
  HLT: { industry: "Healthcare", sources: ["hl7"], note: "Administrative reference designs, not clinical advice, a certified EHR, a FHIR server or an interoperability profile." },
  PHM: { industry: "Pharmaceuticals", sources: ["ich", "gs1"], note: "Manufacturing and quality examples, not a validated system or a claim of GMP, GxP or regulatory compliance." },
  RTL: { industry: "Retail and Distribution", sources: ["gs1"], note: "A proposed eighth industry implemented for review; valuation, tax and promotion rules remain enterprise-specific." },
};

/** Resolve only explicitly declared, stable template references. Destination model
 * identity is still selected by the user through instantiateBlock dependency ports. */
export function industryCatalogue(baseline: Record<string, BlockEntity[]>): IndustryModel[] {
  const all = { ...baseline, ...industryDefinitions };
  const owner = new Map<string, { id: string; entity: BlockEntity }>();
  for (const [id, entities] of Object.entries(all)) for (const entity of entities) {
    if (owner.has(entity.name)) throw new Error(`Duplicate building-block template entity ${entity.name}`);
    owner.set(entity.name, { id, entity });
  }
  return industryInventory.map(row => {
    const pack = packs[row.id.split("-")[1]], entities = industryDefinitions[row.id];
    if (!entities?.length) throw new Error(`Missing industry model ${row.id}`);
    const dependencies = new Set<string>();
    for (const entity of entities) for (const column of entity.columns) if (column.reference) {
      const [table, field, extra] = column.reference.split(".");
      const target = owner.get(table);
      if (!target || extra || !target.entity.columns.some(c => c.name === field)) throw new Error(`Unresolved template port ${entity.name}.${column.name} -> ${column.reference}`);
      if (target.id !== row.id) dependencies.add(target.id);
    }
    return {
      ...row, industry: pack.industry, dependencies: [...dependencies].sort(), entities,
      tags: [pack.industry, ...entities.map(e => e.title)],
      sourceReferences: pack.sources.map(key => sources[key]),
      assumptions: [...commonAssumptions, pack.note],
    };
  });
}
